import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  PageHeader,
  StatusChip,
  Tabs,
  useToast,
} from '@/design/components'
import type { Column } from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { useBreadcrumb } from '@/app/Breadcrumb'
import { ApiError } from '@/lib/api'
import { EMPTY, formatDate, formatDueLabel } from '@/lib/format'
import { cn } from '@/lib/cn'
import { EditTaskModal } from '../components/EditTaskModal'
import { NewTaskModal } from '../components/NewTaskModal'
import { useCompleteTask, useTasks } from '../hooks'
import { OPEN_STATUSES, STATUS_LABEL, STATUS_VARIANT } from '../status'
import type { Task } from '../types'

const PAGE_SIZE = 50

/** Ekran „Zadania" — przydział pracy w sprawach (moje / wszystkie, tylko otwarte). */
export function TasksPage() {
  const { can } = useMe()
  const toast = useToast()
  useBreadcrumb(['Sprawy', 'Zadania'])

  const canManage = can('tasks.manage')

  const [page, setPage] = useState(1)
  // Domyślnie „Moje" + „tylko otwarte" — codzienny widok prawnika (mockup badge'a).
  const [mine, setMine] = useState(true)
  const [openOnly, setOpenOnly] = useState(true)
  const [newOpen, setNewOpen] = useState(false)
  const [prefillCaseId, setPrefillCaseId] = useState<number | null>(null)
  const [selected, setSelected] = useState<Task | null>(null)

  // Wejście z karty sprawy („Dodaj zadanie" → /zadania?sprawa=<id>&nowa=1) otwiera modal
  // z wybraną sprawą i czyści query param — wzorzec `?nowa=1` z modułu timesheet.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('nowa') !== '1') return
    const caseParam = Number(searchParams.get('sprawa'))
    const next = new URLSearchParams(searchParams)
    next.delete('nowa')
    next.delete('sprawa')
    setSearchParams(next, { replace: true })
    if (can('tasks.manage')) {
      setPrefillCaseId(Number.isInteger(caseParam) && caseParam > 0 ? caseParam : null)
      setNewOpen(true)
    }
  }, [searchParams, setSearchParams, can])

  // Wejście z karty sprawy przez kliknięcie WIERSZA (`/zadania?sprawa=<id>`, bez
  // `nowa=1`) zawęża listę do tej sprawy. Bez tego klik z karty sprawy lądował na
  // pełnej liście i wyglądał, jakby nic nie zrobił.
  const caseFilter = (() => {
    if (searchParams.get('nowa') === '1') return null
    const raw = Number(searchParams.get('sprawa'))
    return Number.isInteger(raw) && raw > 0 ? raw : null
  })()

  const listing = useTasks({
    page,
    size: PAGE_SIZE,
    case_id: caseFilter ?? undefined,
    // Przy zawężeniu do sprawy „Moje" nie ma sensu: z karty sprawy chcemy zobaczyć
    // WSZYSTKIE jej zadania, także cudze.
    mine: caseFilter ? undefined : mine || undefined,
    open_only: openOnly || undefined,
  })

  const clearCaseFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('sprawa')
    setSearchParams(next, { replace: true })
    setPage(1)
  }
  const caseFilterLabel = listing.data?.items.find((t) => t.case)?.case?.signature
  const complete = useCompleteTask()

  const finish = async (task: Task) => {
    try {
      await complete.mutateAsync(task.id)
      toast.success('Zadanie zakończone')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.userMessage : 'Nie udało się zakończyć zadania.')
    }
  }

  const columns: Column<Task>[] = [
    {
      key: 'title',
      header: 'Zadanie',
      render: (row) => (
        <div className="min-w-0 leading-tight">
          <p className="truncate font-medium text-ink">{row.title}</p>
          {row.description ? (
            <p className="mt-0.5 max-w-[420px] truncate text-small text-muted">{row.description}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'case',
      header: 'Sprawa',
      width: '220px',
      render: (row) =>
        row.case ? (
          <div className="min-w-0 leading-tight">
            <p className="t-mono truncate text-ink">{row.case.signature}</p>
            <p className="mt-0.5 truncate text-small text-muted">{row.case.client_name}</p>
          </div>
        ) : (
          EMPTY
        ),
    },
    {
      key: 'assignee',
      header: 'Przypisany',
      width: '170px',
      render: (row) =>
        row.assignee ? (
          <span className="flex items-center gap-2">
            <Avatar name={row.assignee.name} size="sm" />
            <span className="truncate text-ink">{row.assignee.short_name}</span>
          </span>
        ) : (
          <span className="text-muted">nieprzypisane</span>
        ),
    },
    {
      key: 'due_date',
      header: 'Termin',
      width: '150px',
      render: (row) => {
        if (!row.due_date) return <span className="text-muted">{EMPTY}</span>
        const overdue = (row.days_left ?? 0) < 0 && OPEN_STATUSES.includes(row.status)
        return (
          <div className="leading-tight">
            <p className={cn('whitespace-nowrap', overdue ? 'font-semibold text-danger' : 'text-ink')}>
              {formatDate(row.due_date)}
            </p>
            <p className={cn('mt-0.5 text-small', overdue ? 'text-danger' : 'text-muted')}>
              {formatDueLabel(row.due_date)}
            </p>
          </div>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '130px',
      render: (row) => (
        <StatusChip size="sm" variant={STATUS_VARIANT[row.status]}>
          {STATUS_LABEL[row.status]}
        </StatusChip>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '110px',
      align: 'right',
      render: (row) =>
        canManage && OPEN_STATUSES.includes(row.status) ? (
          <Button
            size="sm"
            variant="secondary"
            loading={complete.isPending && complete.variables === row.id}
            onClick={(event) => {
              event.stopPropagation()
              void finish(row)
            }}
          >
            Zakończ
          </Button>
        ) : null,
    },
  ]

  return (
    <>
      <PageHeader
        title="Zadania"
        subtitle="Przydział pracy w ramach spraw — zadania ręczne i te utworzone z dekretacji pism."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => { setPrefillCaseId(null); setNewOpen(true) }}>
              Nowe zadanie
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        {caseFilter ? (
          <span className="flex items-center gap-2 rounded border border-line bg-card px-2.5 py-1 text-small text-ink">
            Sprawa: <span className="t-mono">{caseFilterLabel ?? `#${caseFilter}`}</span>
            <button
              type="button"
              aria-label="Wyczyść zawężenie do sprawy"
              onClick={clearCaseFilter}
              className="text-muted transition-colors hover:text-ink"
            >
              ✕
            </button>
          </span>
        ) : null}
        <Tabs
          variant="pills"
          // Przy zawężeniu do sprawy filtr „Moje" jest wyłączony (patrz `useTasks`
          // wyżej), więc przełącznik ma pokazywać stan faktyczny, nie zapamiętany.
          value={caseFilter ? 'all' : mine ? 'mine' : 'all'}
          onChange={(key) => {
            setMine(key === 'mine')
            setPage(1)
          }}
          items={[
            { key: 'mine', label: 'Moje' },
            { key: 'all', label: 'Wszystkie' },
          ]}
        />
        <label className="flex cursor-pointer items-center gap-2 text-base">
          <input
            type="checkbox"
            className="h-[14px] w-[14px] accent-navy-900"
            checked={openOnly}
            onChange={(event) => {
              setOpenOnly(event.target.checked)
              setPage(1)
            }}
          />
          <span className="text-ink">tylko otwarte</span>
        </label>
      </div>

      <DataTable<Task>
        columns={columns}
        rows={listing.data?.items ?? []}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelected(row)}
        total={listing.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={listing.isPending}
        empty={
          <EmptyState
            title={mine ? 'Brak zadań przypisanych do Ciebie' : 'Brak zadań'}
            description="Zadania pojawią się po ręcznym dodaniu albo po dekretacji pisma na prawnika."
            action={
              canManage ? (
                <Button variant="primary" onClick={() => { setPrefillCaseId(null); setNewOpen(true) }}>
                  Nowe zadanie
                </Button>
              ) : null
            }
          />
        }
      />

      <NewTaskModal open={newOpen} onClose={() => setNewOpen(false)} initialCaseId={prefillCaseId} />
      <EditTaskModal task={selected} canManage={canManage} onClose={() => setSelected(null)} />
    </>
  )
}
